<template>
    <div class="space-y-6 pb-8">
        <div class="-mb-4 pl-2 text-2xl font-semibold tracking-wide">
            {{ planTitle }}
        </div>

        <plan-presentation
            v-if="!isEditing"
            :plan="planData"
            :allow-bookings="allowBookings"
        />

        <template v-if="allowBookings">
            <duplicate-contract-message
                :plan-id="planData.id"
                :contract-id="contractData?.id"
                warning-text="You already have a booking for this program in your cart"
                edit-text="Edit Booking"
            />

            <select-recurring-program-start
                :is-active-section="activeSection === 0"
                :contract="contractData"
                :plan="planData"
                :is-mutating-contract="isCreatingContract || isChangingContract"
                @create-contract="handleCreateContract"
                @change-contract="handleChangeContract"
                @edit-section="activeSection = 0"
            />

            <template v-if="contractData !== undefined">
                <participant-details
                    v-if="activeSection > 0"
                    :is-active-section="activeSection === 1"
                    :contract="contractData"
                    :plan="planData"
                    :is-editing="isEditing"
                    :is-updating-contract="
                        isUpdatingContract || isActivatingContract
                    "
                    @continue="handleContinue"
                    @add-to-cart="handleAddToCartParticipantDetails"
                    @activate-contract-now="handleActivateContractNow"
                    @edit-section="activeSection = 1"
                />

                <checkout-section
                    v-if="isKiosk && activeSection > 1"
                    :contract="contractData"
                />

                <document-section
                    v-if="!isKiosk && activeSection > 1"
                    :contract="contractData"
                    :is-signing-document="isSigningDocument"
                    :invalid-initials="invalidInitials"
                    :invalid-signatures="invalidSignatures"
                    @add-to-cart="addToCart"
                    @sign-document="handleSignDocument"
                />
            </template>
        </template>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const PurchaseRecurringProgramPlanFragment = graphql(`
    fragment PurchaseRecurringProgramPlanFragment on Plan {
        id
        name
        planType
        checkAllowBookings: bookingLimit(input: $input) {
            allowBookings
        }
        presentation(input: $input, language: ENGLISH) {
            title
        }
        ...PlanPresentationPlanFragment
        ...SelectRecurringProgramStartPlanFragment
        ...ParticipantDetailsPlanFragment
    }
`)

export const PurchaseRecurringProgramContractFragment = graphql(`
    fragment PurchaseRecurringProgramContractFragment on PlanContract {
        id
        draftExpiresAtUtc
        updatedAtUtc
        readyForPurchase(input: { checkDocument: true })
        ...SelectRecurringProgramStartContractFragment
        ...ParticipantDetailsContractFragment
        ...DocumentSectionContractFragment
        ...PaymentMethodInputPlanContractFragment
    }
`)
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import SelectRecurringProgramStart from '~/components/PurchaseContract/Program/SelectRecurringProgramStart.vue'
import ParticipantDetails from '~/components/PurchaseContract/ParticipantDetails/ParticipantDetails.vue'
import DocumentSection from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import {
    setCartVisible,
    useRedirectOnContractDelete,
    useSetCartData
} from '~/composables/use-cart'
import { useRouter } from 'vue-router'
import type {
    ChangeContractInput,
    CreateContractInput,
    SignDocumentInput,
    UpdateContractInput
} from '~/resources/graphql/graphql'
import DuplicateContractMessage from '~/components/PurchaseContract/DuplicateContractMessage.vue'
import { useRouteLink } from '~/composables/routing'
import PlanPresentation from '~/components/PurchaseContract/PlanPresentation.vue'
import { useActivateContractMutation } from '~/components/PurchaseContract/use-activate-contract-mutation'
import { useToast } from 'primevue/usetoast'
import CheckoutSection from '~/components/PurchaseContract/CheckoutSection/CheckoutSection.vue'
import { useLayoutMode } from '~/composables/use-layout-mode'
import { useQueryClient } from '@tanstack/vue-query'
import { useStorefrontPrefix } from '~/composables/use-storefront-prefix'
import { useRecurringProgramCreateContractMutation } from '~/components/PurchaseContract/Program/use-recurring-program-create-contract-mutation'
import { useRecurringProgramChangeContractMutation } from '~/components/PurchaseContract/Program/use-recurring-program-change-contract-mutation'
import { useRecurringProgramUpdateContractMutation } from '~/components/PurchaseContract/Program/use-recurring-program-update-contract-mutation'
import { useRecurringProgramSignDocumentMutation } from '~/components/PurchaseContract/Program/use-recurring-program-sign-document-mutation'
import { getEditingActiveSection } from '~/components/PurchaseContract/purchase-contract-helpers'
import { useOrgShortTitle } from '~/composables/use-org'
import { useHead } from 'unhead'
import { useShowAlert } from '#imports'
import { handleValidationErrors } from '~/components/PurchaseContract/DocumentSection/handle-validation-errors'
import { useAddToCartAnalytics } from '~/composables/analytics/add-to-cart'

const props = defineProps<{
    plan: FragmentType<typeof PurchaseRecurringProgramPlanFragment>
    contract:
        | FragmentType<typeof PurchaseRecurringProgramContractFragment>
        | undefined
    isEditing: boolean
}>()

const emit = defineEmits<{
    (
        e: 'update:contract',
        payload:
            | FragmentType<typeof PurchaseRecurringProgramContractFragment>
            | undefined
    ): void
}>()

const { isKiosk } = useLayoutMode()
const { emitAddToCartEvent } = useAddToCartAnalytics()

const planData = computed(() =>
    getFragmentData(PurchaseRecurringProgramPlanFragment, props.plan)
)

const planTitle = computed(() => {
    if (
        planData.value.presentation !== null &&
        planData.value.presentation.title !== ''
    ) {
        return planData.value.presentation.title
    }

    return planData.value.name
})

const shortTitle = useOrgShortTitle()
useHead({
    title: () => shortTitle.value + ' | ' + planTitle.value
})

const allowBookings = computed(
    () => planData.value.checkAllowBookings.allowBookings
)

const contractData = computed(() =>
    props.contract !== undefined
        ? getFragmentData(
              PurchaseRecurringProgramContractFragment,
              props.contract
          )
        : undefined
)

const activeSection = ref(0)
function setEditingActiveSection() {
    // If we are not editing, return
    if (contractData.value === undefined) {
        return
    }

    activeSection.value = getEditingActiveSection(
        contractData.value.draftExpiresAtUtc,
        contractData.value.readyForPurchase
    )
}
// Run our function on setup
setEditingActiveSection()

const router = useRouter()
const storefrontPrefix = useStorefrontPrefix()
const { storefrontHomeLink } = useRouteLink()
function addToCart() {
    setCartVisible(true)
    router.push(storefrontHomeLink(storefrontPrefix.value))
}

const { mutate: createContract, isPending: isCreatingContract } =
    useRecurringProgramCreateContractMutation()
const queryClient = useQueryClient()
function handleCreateContract(input: CreateContractInput) {
    createContract(input, {
        onSuccess: data => {
            emit('update:contract', data)
            activeSection.value = 1
            queryClient.invalidateQueries({
                predicate: query =>
                    query.queryKey.includes(
                        'SelectRecurringProgramCourseCoursesQuery'
                    )
            })
        }
    })
}

const { mutate: changeContract, isPending: isChangingContract } =
    useRecurringProgramChangeContractMutation()

function handleChangeContract(input: ChangeContractInput) {
    changeContract(input, {
        onSuccess: data => {
            if (data.changeContract.__typename === 'ChangeContractResult') {
                if (data.changeContract.record.__typename === 'PlanContract') {
                    emit('update:contract', data.changeContract.record)
                    activeSection.value = 1
                    queryClient.invalidateQueries({
                        predicate: query =>
                            query.queryKey.includes(
                                'SelectRecurringProgramCourseCoursesQuery'
                            )
                    })
                    return
                }
            }

            throw new Error()
        }
    })
}

const { mutateAsync: updateContract, isPending: isUpdatingContract } =
    useRecurringProgramUpdateContractMutation()
const setCartData = useSetCartData()
function handleContinue(input: UpdateContractInput) {
    updateContract(input, {
        onSuccess: data => {
            if (data.updateContract.__typename === 'UpdateContractResult') {
                // Update the cart data
                if (data.updateContract.query.viewer !== null) {
                    setCartData(data.updateContract.query.viewer.cart)
                }
                if (data.updateContract.record.__typename === 'PlanContract') {
                    emit('update:contract', data.updateContract.record)
                    activeSection.value = 2
                    return
                }
            }

            throw new Error()
        }
    })
}

function handleAddToCartParticipantDetails(input: UpdateContractInput) {
    updateContract(input, {
        onSuccess: data => {
            if (data.updateContract.__typename === 'UpdateContractResult') {
                // Update the cart data
                if (data.updateContract.query.viewer !== null) {
                    setCartData(data.updateContract.query.viewer.cart)
                }

                if (data.updateContract.record.__typename === 'PlanContract') {
                    emitAddToCartEvent(data.updateContract.record)
                }

                addToCart()
                return
            }

            throw new Error()
        }
    })
}

const showAlert = useShowAlert()
const toast = useToast()
const { mutate: activateContractMutate, isPending: isActivatingContract } =
    useActivateContractMutation()
async function handleActivateContractNow(input: UpdateContractInput) {
    await updateContract(input, {
        onSuccess: data => {
            if (
                data.updateContract.__typename === 'UpdateContractResult' &&
                data.updateContract.record.__typename === 'PlanContract'
            ) {
                if (data.updateContract.record.__typename === 'PlanContract') {
                    emit('update:contract', data.updateContract.record)
                    return
                }
            }

            throw new Error()
        }
    })

    if (contractData.value === undefined) {
        throw new Error()
    }

    activateContractMutate(
        {
            id: contractData.value.id,
            updatedAtUtc: contractData.value.updatedAtUtc,
            documentInput: null
        },
        {
            onSuccess: data => {
                if (
                    data.__typename === 'ActivateContractResult' ||
                    data.__typename === 'ContractIsActive'
                ) {
                    toast.add({
                        severity: 'success',
                        summary: 'Thank you for your purchase!',
                        detail: planTitle.value,
                        life: 10000,
                        closable: false
                    })
                    router.push(storefrontHomeLink(storefrontPrefix.value))
                } else {
                    showAlert(
                        'Whoops!',
                        'Something was not quite right. Please try again and if the issue persists, contact the facility to resolve'
                    )
                }
            }
        }
    )
}

const invalidInitials = ref(new Set<number>())
const invalidSignatures = ref(new Set<number>())

const { mutate: signDocument, isPending: isSigningDocument } =
    useRecurringProgramSignDocumentMutation()
function handleSignDocument(mutationData: SignDocumentInput) {
    signDocument(mutationData, {
        onSuccess: data => {
            if (data.signDocument.__typename === 'ValidationError') {
                handleValidationErrors(
                    data.signDocument.fieldErrors,
                    invalidInitials,
                    invalidSignatures
                )
                return
            }

            if (data.signDocument.__typename === 'SignDocumentResult') {
                // Update the cart data
                if (data.signDocument.query.viewer !== null) {
                    setCartData(data.signDocument.query.viewer.cart)
                }

                if (data.signDocument.record.__typename === 'PlanContract') {
                    emit('update:contract', data.signDocument.record)
                    return
                }
            }

            throw new Error()
        }
    })
}

watch(
    () => planData.value.id,
    (newValue, oldValue) => {
        if (newValue !== oldValue) {
            // close all the components
            activeSection.value = 0
            // Remove contract data
            emit('update:contract', undefined)
        }
    }
)

useRedirectOnContractDelete(() => contractData.value?.id)
</script>
